import { getPercusionIds, PERCUSION_CORPORAL, type PercusionBucketRect } from '../percusionCorporal';
import type { RhythmFigureId } from '../rhythm/rhythmTokens';

export interface RhythmBubble {
  id: string;
  percusionId: string;
  figureId: RhythmFigureId;
  lane: number;
  y: number;
  speedPxPerMs: number;
  radius: number;
  hit: boolean;
  missed: boolean;
  popT: number;
  captureY?: number;
}

export type AtrapaRitmoPhase = 'lobby' | 'playing' | 'finished';

export interface AtrapaRitmoDifficulty {
  id: 'facil' | 'medio' | 'rapido';
  label: string;
  emoji: string;
  description: string;
  bpm: number;
  durationMs: number;
  accent: 'mint' | 'sky' | 'coral';
}

export interface AtrapaRitmoState {
  phase: AtrapaRitmoPhase;
  difficulty: AtrapaRitmoDifficulty;
  score: number;
  streak: number;
  maxStreak: number;
  timeLeftMs: number;
  bubbles: RhythmBubble[];
  hits: number;
  misses: number;
  spawnCooldownMs: number;
  lastFeedback: 'hit' | 'miss' | 'combo' | null;
  feedbackUntil: number;
  bucketFlashUntil: Record<number, number>;
}

export const ATRAPA_RITMO_DIFFICULTIES: AtrapaRitmoDifficulty[] = [
  {
    id: 'facil',
    label: 'Tortuga',
    emoji: '🐢',
    description: '75 s · muy lento · para calentar',
    bpm: 52,
    durationMs: 75_000,
    accent: 'mint',
  },
  {
    id: 'medio',
    label: 'En marcha',
    emoji: '🏃',
    description: '60 s · ritmo medio · sube la racha',
    bpm: 80,
    durationMs: 60_000,
    accent: 'sky',
  },
  {
    id: 'rapido',
    label: 'Turbo',
    emoji: '🚀',
    description: '45 s · rápido · para expertos',
    bpm: 108,
    durationMs: 45_000,
    accent: 'coral',
  },
];

const SPEED_MULT: Record<AtrapaRitmoDifficulty['id'], number> = {
  facil: 0.55,
  medio: 0.82,
  rapido: 1.08,
};

export const RHYTHM_BUBBLE_RADIUS_PX = 30;
export const RHYTHM_PIXELS_PER_BEAT = RHYTHM_BUBBLE_RADIUS_PX * 2 * 3;
export const RHYTHM_LANE_TOP_Y = 8;

let bubbleCounter = 0;

export function speedFromBpm(bpm: number, difficultyId?: AtrapaRitmoDifficulty['id']): number {
  const msPerBeat = 60_000 / bpm;
  const base = RHYTHM_PIXELS_PER_BEAT / msPerBeat;
  const mult = difficultyId ? SPEED_MULT[difficultyId] ?? 1 : 1;
  return base * mult;
}

export function createAtrapaRitmoState(
  difficulty: AtrapaRitmoDifficulty = ATRAPA_RITMO_DIFFICULTIES[0]!
): AtrapaRitmoState {
  return {
    phase: 'lobby',
    difficulty,
    score: 0,
    streak: 0,
    maxStreak: 0,
    timeLeftMs: difficulty.durationMs,
    bubbles: [],
    hits: 0,
    misses: 0,
    spawnCooldownMs: 0,
    lastFeedback: null,
    feedbackUntil: 0,
    bucketFlashUntil: {},
  };
}

export function startAtrapaRitmo(state: AtrapaRitmoState): AtrapaRitmoState {
  const d = state.difficulty;
  return {
    ...createAtrapaRitmoState(d),
    phase: 'playing',
    spawnCooldownMs: 60_000 / d.bpm,
  };
}

const MAX_ACTIVE_BUBBLES = 6;
const SPAWN_RAMP_MS = 14_000;

function maxActiveBubbles(elapsedMs: number): number {
  if (elapsedMs >= SPAWN_RAMP_MS) return MAX_ACTIVE_BUBBLES;
  return Math.max(2, Math.min(MAX_ACTIVE_BUBBLES, 2 + Math.floor(elapsedMs / 2000)));
}

function spawnBubble(difficulty: AtrapaRitmoDifficulty): RhythmBubble {
  const ids = getPercusionIds();
  const percusionId = ids[Math.floor(Math.random() * ids.length)]!;
  const item = PERCUSION_CORPORAL.find((p) => p.id === percusionId);
  const lane = PERCUSION_CORPORAL.findIndex((p) => p.id === percusionId);
  return {
    id: `rb-${++bubbleCounter}`,
    percusionId,
    figureId: item?.figureId ?? 'negra',
    lane: lane >= 0 ? lane : 0,
    y: RHYTHM_LANE_TOP_Y,
    speedPxPerMs: speedFromBpm(difficulty.bpm, difficulty.id),
    radius: RHYTHM_BUBBLE_RADIUS_PX,
    hit: false,
    missed: false,
    popT: 0,
  };
}

function withFeedback(
  state: AtrapaRitmoState,
  kind: 'hit' | 'miss' | 'combo',
  now: number
): AtrapaRitmoState {
  return { ...state, lastFeedback: kind, feedbackUntil: now + (kind === 'combo' ? 500 : 280) };
}

export function bucketRectForPercusion(
  rects: PercusionBucketRect[],
  percusionId: string
): PercusionBucketRect | undefined {
  return rects.find((r) => r.id === percusionId);
}

export function bubbleMaxCenterY(rect: PercusionBucketRect, radius: number): number {
  return rect.y + rect.h - radius;
}

export function bubbleInBucket(b: RhythmBubble, rect: PercusionBucketRect): boolean {
  const top = b.y - b.radius;
  const bottom = b.y + b.radius;
  return bottom >= rect.y && top <= rect.y + rect.h;
}

export function advanceRhythmBubble(
  b: RhythmBubble,
  dtMs: number,
  rect: PercusionBucketRect | undefined
): RhythmBubble {
  if (b.hit) {
    return { ...b, popT: Math.min(1, b.popT + dtMs / 220) };
  }
  if (b.missed) {
    return { ...b, popT: Math.min(1, b.popT + dtMs / 380) };
  }
  let y = b.y + b.speedPxPerMs * dtMs;
  if (rect) {
    y = Math.min(y, bubbleMaxCenterY(rect, b.radius));
  }
  return { ...b, y };
}

export function shouldMarkBubbleMiss(b: RhythmBubble, rect: PercusionBucketRect | undefined): boolean {
  if (b.hit || b.missed || !rect) return false;
  return b.y + b.radius >= rect.y + rect.h - 0.5;
}

function pruneBucketFlash(
  flash: Record<number, number>,
  now: number
): Record<number, number> {
  const next: Record<number, number> = {};
  for (const [lane, until] of Object.entries(flash)) {
    if (until > now) next[Number(lane)] = until;
  }
  return next;
}

export function tickAtrapaRitmo(
  state: AtrapaRitmoState,
  dtMs: number,
  playfieldHeight: number,
  bucketRects: PercusionBucketRect[],
  now = performance.now()
): AtrapaRitmoState {
  if (state.phase !== 'playing') return state;

  let timeLeftMs = state.timeLeftMs - dtMs;
  let score = state.score;
  let streak = state.streak;
  let maxStreak = state.maxStreak;
  let hits = state.hits;
  let misses = state.misses;
  let spawnCooldownMs = state.spawnCooldownMs - dtMs;
  let lastFeedback = state.lastFeedback;
  let feedbackUntil = state.feedbackUntil;
  let bucketFlashUntil = pruneBucketFlash(state.bucketFlashUntil, now);

  if (feedbackUntil <= now) {
    lastFeedback = null;
  }

  const rectById = Object.fromEntries(bucketRects.map((r) => [r.id, r]));

  let bubbles = state.bubbles.map((b) =>
    advanceRhythmBubble(b, dtMs, rectById[b.percusionId])
  );

  if (spawnCooldownMs <= 0) {
    const elapsedMs = state.difficulty.durationMs - timeLeftMs;
    const cap = maxActiveBubbles(elapsedMs);
    const active = bubbles.filter((b) => !b.hit && !b.missed).length;
    if (active < cap) {
      bubbles.push(spawnBubble(state.difficulty));
    }
    spawnCooldownMs = 60_000 / state.difficulty.bpm;
  }

  for (const b of bubbles) {
    if (shouldMarkBubbleMiss(b, rectById[b.percusionId])) {
      b.missed = true;
      b.popT = 0;
      streak = 0;
      misses += 1;
      score = Math.max(0, score - 8);
      lastFeedback = 'miss';
      feedbackUntil = now + 280;
    }
  }

  bubbles = bubbles.filter(
    (b) =>
      (b.hit || b.missed ? b.popT < 1 : true) &&
      b.y < playfieldHeight + 40
  );

  if (timeLeftMs <= 0) {
    return {
      ...state,
      phase: 'finished',
      timeLeftMs: 0,
      score,
      streak,
      maxStreak,
      hits,
      misses,
      bubbles: bubbles.filter((b) => !b.missed),
      spawnCooldownMs,
      lastFeedback,
      feedbackUntil,
      bucketFlashUntil,
    };
  }

  return {
    ...state,
    timeLeftMs,
    score,
    streak,
    maxStreak,
    hits,
    misses,
    bubbles,
    spawnCooldownMs,
    lastFeedback,
    feedbackUntil,
    bucketFlashUntil,
  };
}

export function tryHitRhythmBubble(
  state: AtrapaRitmoState,
  percusionId: string,
  bucketRects: PercusionBucketRect[],
  now = performance.now()
): AtrapaRitmoState {
  if (state.phase !== 'playing') return state;

  const rect = bucketRectForPercusion(bucketRects, percusionId);
  if (!rect) return state;

  const candidates = state.bubbles.filter(
    (b) =>
      !b.hit &&
      !b.missed &&
      b.percusionId === percusionId &&
      bubbleInBucket(b, rect)
  );
  if (!candidates.length) return state;

  const target = candidates.sort((a, b) => b.y - a.y)[0]!;
  const captureY = rect.y + rect.h * 0.52;
  const dist = Math.abs(target.y - captureY);
  const perfect = dist < 24;

  const bubbles = state.bubbles.map((b) =>
    b.id === target.id ? { ...b, hit: true, popT: 0, captureY, y: target.y } : b
  );
  const streak = state.streak + 1;
  const maxStreak = Math.max(state.maxStreak, streak);
  const base = perfect ? 15 : 10;
  const bonus = Math.min(streak, 12);
  const combo = streak >= 5;

  let next = {
    ...state,
    bubbles,
    streak,
    maxStreak,
    hits: state.hits + 1,
    score: state.score + base + bonus,
    bucketFlashUntil: {
      ...state.bucketFlashUntil,
      [target.lane]: now + 620,
    },
  };
  next = withFeedback(next, combo ? 'combo' : 'hit', now);
  return next;
}
